import { FC, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import { deleteGolfer } from '@/api/golfer/golfer.actions.ts';
import {
  GolferStatsData,
  GolferTypeDisplayMap,
  GolferDetails,
  GolferType,
} from '@/api/golfer/golfer.types.ts';
import homeIcon from '@/assets/homeIcon.png';
import CustomEntityTable, {
  Column,
} from '@/components/controls/CustomEntityTable.tsx';
import GolferDetailsModal from '@/components/layout/adminDashboardPage/GolferDetailsModal.tsx';
import Pagination from '@/components/layout/Pagination.tsx';
import CustomConfirmToast from '@/components/toast/CustomConfirmToast.tsx';
import { useGolfersStats } from '@/hooks/useGolfersStats.ts';
import countries from '@/lib/countries.ts';

const dashboardColumns: Column<GolferStatsData>[] = [
  { key: 'name', label: 'Golfer Name', sortable: true, width: 20 },
  {
    key: 'type',
    label: 'Account Type',
    sortable: true,
    formattingFunc: (value) => GolferTypeDisplayMap[value],
    width: 20,
  },
  {
    key: 'golfRegistrationNumber',
    label: 'Registration No.',
    sortable: false,
    width: 20,
  },
  { key: 'country', label: 'Country', sortable: false, width: 20 },
  {
    key: 'holeInOnes',
    label: 'Hole-in-Ones',
    sortable: true,
    formattingFunc: (value) => (value === 0 ? 'None' : String(value)),
    width: 20,
  },
];

const AdminDashboard: FC = () => {
  const {
    golfersStats,
    golfersCount,
    isFetchingAll,
    refetch,
    pageSize,
    currentPage,
    setCurrentPage,
  } = useGolfersStats();

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedGolfer, setSelectedGolfer] = useState<GolferDetails | null>(
    null
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [golferToDelete, setGolferToDelete] = useState<GolferStatsData | null>(
    null
  );

  const tableData = useMemo(
    () =>
      golfersStats.map((golfer: GolferStatsData) => ({
        ...golfer,
        name: `${golfer.name} ${golfer.surname}`,
        golfRegistrationNumber: golfer.golfRegistrationNumber || 'N/A',
        country:
          countries.find((c) => c.code === golfer.country)?.label || 'N/A',
      })),
    [golfersStats]
  );

  const handleViewClick = (golfer: GolferStatsData): void => {
    const details: GolferDetails = {
      id: golfer.id,
      name: golfer.name,
      mobileNumber: golfer.mobileNumber,
      email: golfer.email,
      accountType: GolferTypeDisplayMap[golfer.type as GolferType],
      registrationNo: golfer.golfRegistrationNumber,
      country: golfer.country,
      holeInOnes: golfer.holeInOnes === 0 ? 'None' : String(golfer.holeInOnes),
    };

    setSelectedGolfer(details);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = (): void => {
    setSelectedGolfer(null);
    setShowDetailsModal(false);
  };

  const handleDeleteClick = (golfer: GolferStatsData): void => {
    setGolferToDelete(golfer);
    setShowDeleteModal(true);
  };

  const cancelDelete = (): void => {
    setShowDeleteModal(false);
    setGolferToDelete(null);
  };

  const confirmDelete = async (): Promise<void> => {
    setShowDeleteModal(false);
    if (!golferToDelete) {
      return;
    }

    const response = await deleteGolfer(golferToDelete.id);
    if (response.success) {
      toast.success('Golfer successfully deleted.');
    } else {
      toast.error(response.error.message);
    }

    setGolferToDelete(null);
    await refetch(currentPage);
  };

  return (
    <>
      <CustomEntityTable
        title="Golfers Overview"
        columns={dashboardColumns}
        data={tableData}
        totalData={golfersCount}
        iconName={homeIcon}
        isLoading={isFetchingAll}
        onView={handleViewClick}
        onDelete={handleDeleteClick}
        deleteTooltipText="Delete Golfer"
      />
      <Pagination
        totalItems={golfersCount}
        pageSize={pageSize}
        currentPage={currentPage}
        goToPage={setCurrentPage}
      />

      {selectedGolfer ? (
        <GolferDetailsModal
          isOpen={showDetailsModal}
          onClose={handleCloseDetailsModal}
          details={selectedGolfer}
        />
      ) : null}

      <CustomConfirmToast
        header="Confirm Delete"
        show={showDeleteModal}
        message="Are you sure you want to delete this golfer?"
        onAccept={confirmDelete}
        onDecline={cancelDelete}
      />
    </>
  );
};

export default AdminDashboard;
